import { YES_NO_DETAIL_FIELD_PAIRS } from '../types/preScanQuestionnaire';

export type QuestionnaireField = {
  label: string;
  key: string;
};

export type QuestionnaireSection = {
  title: string;
  fields: QuestionnaireField[];
};

export const PRE_SCAN_QUESTIONNAIRE_SECTIONS: QuestionnaireSection[] = [
  {
    title: 'Medical History',
    fields: [
      {
        label: 'Do you have any past or ongoing medical conditions we should know about?',
        key: 'past_ongoing_medical_conditions',
      },
      {
        label: 'Have you had any previous surgeries or medical procedures?',
        key: 'any_surgery_past_3_month',
      },
      {
        label: 'Are you currently taking any medications or supplements?',
        key: 'medications_supplements',
      },
    ],
  },
  {
    title: 'Cancer History',
    fields: [
      { label: 'Have you ever been diagnosed with cancer?', key: 'cancer_history' },
      {
        label: 'Have you had any previous medical imaging such as an MRI, CT, or X-ray?',
        key: 'any_previous_imaging',
      },
    ],
  },
  {
    title: 'Clinical Concern',
    fields: [
      {
        label: 'What is the primary reason for booking a full body MRI scan with us?',
        key: 'reason_for_scan',
      },
      {
        label: 'Are you currently experiencing any symptoms? (e.g. pain, discomfort etc.)',
        key: 'current_symptoms',
      },
    ],
  },
  {
    title: 'Medical Implants and Metallic Objects',
    fields: [
      { label: 'Any known metallic piercing/object/shrapnel', key: 'metallic_piercing_object_shrapnel' },
      {
        label:
          'Cardiac pacemaker or defibrillator, ICD, cardiac stent or metallic heart valve',
        key: 'pacemaker_defibrillator',
      },
      { label: 'Metalwork/clips/coils after fractures or surgery', key: 'metalwork_fractures_surgery' },
      { label: 'Any joint or limb replacement eg hip/knee', key: 'joint_limb_replacement' },
      { label: 'Contraceptive coil (IUD)', key: 'contraceptive_coil' },
      { label: 'Internal hearing devices or cochlear implants', key: 'hearing_devices' },
      { label: 'Hydrocephalus shunt, spinal shunt or neurostimulator', key: 'hydrocephalus_shunt' },
      { label: 'Any dental implants, fillings, dentures or devices', key: 'dental_implants' },
      { label: 'Any metallic objects or shrapnels in the eye', key: 'metallic_objects_shrapnel_eye' },
      { label: 'Specific types of aneurysm clips', key: 'aneurysm_clips' },
    ],
  },
  {
    title: 'Implant Details',
    fields: [
      {
        label:
          'If you selected yes on anything, please give details (type, location, when inserted).',
        key: 'metallic_implants_details',
      },
    ],
  },
  {
    title: 'Infection Risks or Allergies',
    fields: [
      { label: 'Do you have any infection risk (e.g. MRSA)?', key: 'infection_risk' },
      { label: 'Do you have any allergies?', key: 'any_allergies' },
      { label: 'Are you diabetic?', key: 'diabetic' },
      { label: 'Any special needs eg hearing, mobility, speech or other?', key: 'special_needs' },
      { label: 'Any claustrophobia?', key: 'claustrophobia' },
      {
        label: 'If you selected yes on anything, please provide details.',
        key: 'infection_allergy_details',
      },
    ],
  },
];

export const PRE_SCAN_QUESTIONNAIRE_DISCLAIMER_ITEMS: string[] = [
  'I confirm that all information provided in this questionnaire is accurate and complete to the best of my knowledge.',
  'I understand that providing false or incomplete information may affect the safety and accuracy of my scan.',
  'I confirm I am aged 18 or over.',
  'I understand that any lesion smaller than 5mm may not be identified and that for lungs, breast and the gastrointestinal tract, MRI may not be the most suitable imaging for these areas.',
  'I understand that a full body MRI is a screening tool and does not guarantee detection of all conditions. Results will be reviewed by a clinician and communicated to me directly.',
];

export const PRE_SCAN_QUESTIONNAIRE_FEMALE_DISCLAIMER_ITEM =
  'I confirm I am not pregnant, or if there is any possibility I could be pregnant, I have informed the provider before proceeding.';

export const getPreScanQuestionnaireDisclaimerItems = (
  gender?: string | null
): string[] => {
  const items = [...PRE_SCAN_QUESTIONNAIRE_DISCLAIMER_ITEMS];
  if (gender?.toLowerCase() === 'female') {
    items.splice(2, 0, PRE_SCAN_QUESTIONNAIRE_FEMALE_DISCLAIMER_ITEM);
  }
  return items;
};

export const createInitialDisclaimerConfirmations = (
  gender?: string | null
): Record<string, boolean> =>
  Object.fromEntries(
    getPreScanQuestionnaireDisclaimerItems(gender).map((item) => [item, false])
  );

export const areAllDisclaimerItemsConfirmed = (
  confirmations: Record<string, boolean>,
  gender?: string | null
): boolean =>
  getPreScanQuestionnaireDisclaimerItems(gender).every(
    (item) => confirmations[item] === true
  );

export const isDisclaimerItemConfirmed = (
  scanInfo: Record<string, unknown> | null | undefined,
  item: string
): boolean => {
  if (!scanInfo?.pre_scan_questionnaire_confirmed) {
    return false;
  }

  const confirmations = scanInfo.disclaimer_confirmations;
  if (confirmations && typeof confirmations === 'object' && !Array.isArray(confirmations)) {
    return Boolean((confirmations as Record<string, boolean>)[item]);
  }

  return true;
};

export const displayQuestionnaireValue = (
  scanInfo: Record<string, unknown> | null | undefined,
  key: string
): string => {
  const value = scanInfo?.[key];
  if (value === undefined || value === null || String(value).trim() === '') {
    return '-';
  }

  let answer = String(value).trim();

  const detailsKey = YES_NO_DETAIL_FIELD_PAIRS[key as keyof typeof YES_NO_DETAIL_FIELD_PAIRS];
  if (detailsKey && answer.toLowerCase() === 'yes') {
    const details = displayQuestionnaireValue(scanInfo, detailsKey);
    if (details !== '-') {
      answer = `${answer} — ${details}`;
    }
  }

  return answer;
};

export const formatMemberGender = (gender?: string | null): string => {
  if (!gender) return '-';
  return gender.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};
